"use client";

import Image from "next/image";
import Button from "../../ui/Button";
import Link from "next/link";
import { useMemo } from "react";
import { SponsoredBanners } from "@/components";
import { useSponsoredBanners } from "@/components/providers/SponsoredBannersProvider";
import { getStepStripDesktopShapeClasses } from "./stepStripDesktopShape";

const STEPS = [
  {
    label: "SHOP MENU",
    text: "Browse our legal Calgary weed delivery menu online",
  },
  {
    label: "ADD TO CART",
    text: "Choose flower, vapes, edibles, and more - then add to cart",
  },
  {
    label: "CHECKOUT SECURELY",
    text: "Complete your order online using your credit card",
  },
  {
    label: "WE DELIVER",
    text: "Same-day delivery in 45 minutes with SMS tracking",
  },
] as const;

const DEFAULT_HOW_IT_WORKS_SPONSORED_POSITION = "sameday-delivery-2";

function HowItWorksBannerSlot({ position }: { position: string }) {
  const { banners, loading } = useSponsoredBanners();
  const slotBanners = useMemo(
    () => banners.filter((b) => b.position === position).slice(0, 1),
    [banners, position]
  );

  if (loading || slotBanners.length === 0) {
    return null;
  }

  return (
    <SponsoredBanners
      position={position}
      limit={1}
      banners={slotBanners}
      className="w-full [&_img]:!rounded-2xl"
    />
  );
}

export type HowOurDeliveryWorksSectionProps = {
  /** Sponsored banner slot at the bottom of this section (admin “position”). */
  sponsoredBannerPosition?: string;
};

export default function HowOurDeliveryWorksSection({
  sponsoredBannerPosition = DEFAULT_HOW_IT_WORKS_SPONSORED_POSITION,
}: HowOurDeliveryWorksSectionProps) {
  return (
    <section
      className="w-full max-w-none"
      aria-labelledby="how-our-delivery-heading"
    >
      <div className="w-full overflow-hidden">
        {/* Hero area, illustration first on mobile, text left on lg */}
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-start lg:gap-10">
          <div className="order-2 min-w-0 lg:order-1">
            <h2
              id="how-our-delivery-heading"
              className="text-h3-mobile font-bold text-blue-80 sm:text-h3-desktop"
            >
              How our cannabis delivery work
            </h2>

            <div className="mt-4 space-y-3 text-body-sm leading-relaxed text-text-secondary sm:text-body-md">
              <p>
                Working from home or hanging at a friend&apos;s place, we&apos;ll bring your order to you.
                Dank&apos;s mail-order cannabis service saves you the drive so you can spend your time on
                better things.
              </p>
              <p>
                Order high-quality cannabis products at competitive prices from our website and
                one of our trained fulfillment specialists will pick, pack and send your order out
                for delivery. It&apos;s that easy. Enjoy fast online weed delivery alberta with times
                complete with a tracking number. No need to head in-store or wait in line. We
                provide a hassle-free and convenient way to order Alberta weed delivery.
              </p>
            </div>

            <div className="mt-6 flex max-sm:flex-col gap-3">
              <Button asChild variant="primary" size="lg" className="rounded-xl">
                <Link href="/search">
                  Same day delivery
                </Link>
              </Button>
              <Button asChild variant="primary" size="lg" className="rounded-xl">
                <Link href="/search?delivery=mail-order">
                  Alberta mail order
                </Link>
              </Button>
            </div>
          </div>

          {/* Illustration, framed hero-media.png */}
          <div className="order-1 flex w-full justify-center lg:order-2 lg:justify-end">
            <div
              className="relative z-10 mx-auto aspect-[9/5] w-full overflow-hidden rounded-2xl border-[4px] border-constant-white bg-constant-white p-2 drop-shadow sm:border-[10px] lg:mx-0 lg:ml-auto"
            >
              <Image
                src="/media/pages/sameday-delivery/hero-media.webp"
                alt="Dank cannabis delivery storefront illustration"
                fill
                className="object-contain object-center"
              />
            </div>
          </div>
        </div>

        {/* Step strip, same layout & classes as Calgary Weed Delivery section */}
        <div className="relative w-full overflow-x-auto scrollbar-hide">
          <div className="mt-4 grid grid-cols-4 gap-[10px] min-w-max lg:min-w-0">
            {STEPS.map((step, index) => (
              <article
                key={step.label}
                className={[
                  "relative flex flex-col justify-between overflow-hidden rounded-xl border border-blue-10 bg-constant-white pb-4 pl-5 pr-6 pt-3 min-w-[300px] lg:min-w-0 lg:pl-4 lg:pr-5",
                  "min-h-40",
                  getStepStripDesktopShapeClasses(index, STEPS.length),
                ].join(" ")}
              >
                <span
                  className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[200px] font-black leading-none text-blue-10 select-none"
                  aria-hidden
                >
                  {index + 1}
                </span>
                <span className="relative inline-block w-max rounded-xl border border-blue-10 bg-blue-5 px-3 py-1 text-body-16-desktop font-medium text-blue-60">
                  {step.label}
                </span>
                <p className="relative text-body-20-desktop font-bold leading-snug text-blue-60">
                  {step.text}
                </p>
              </article>
            ))}
          </div>
        </div>

        {/* Sponsored banner */}
        <div className="mt-6">
          <HowItWorksBannerSlot position={sponsoredBannerPosition} />
        </div>
      </div>
    </section>
  );
}
